/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
/**
 * Wing Variant Picker -- 2-level navigation (Colour + Size)
 * Reads variation_data JSON from metafield and renders interactive pills.
 * Uses Impact theme block-swatch classes for visual consistency.
 */
if (!customElements.get("wing-variant-picker")) {
  class WingVariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        const raw = this.querySelector("[data-wing-variation]")?.textContent;
        if (!raw) return;

        this.data = JSON.parse(raw);
        if (!this.data || this.data.type !== "wing") return;

        this.current = { ...this.data.current };
        this.render();
      } catch (e) {
        console.error("[wing-variant-picker] init error:", e);
      }
    }

    /** Extract unique values for a level key from the matrix */
    getValues(levelKey) {
      const values = new Set();
      Object.keys(this.data.matrix).forEach((key) => {
        const parts = key.split("|");
        const levelIndex = this.data.levels.findIndex((l) => l.key === levelKey);
        if (levelIndex >= 0 && parts[levelIndex] !== undefined) {
          values.add(parts[levelIndex]);
        }
      });
      return [...values];
    }

    /** Build matrix key from current selections */
    getMatrixKey(overrides = {}) {
      const sel = { ...this.current, ...overrides };
      return this.data.levels.map((l) => sel[l.key]).join("|");
    }

    /** Check if a specific option combination exists and is available */
    isAvailable(levelKey, value) {
      const key = this.getMatrixKey({ [levelKey]: value });
      const entry = this.data.matrix[key];
      return entry && entry.available !== false;
    }

    /** Color swatch map for known Unifiber wing colorways */
    static SWATCHES = {
      "Blue-Yellow": { bg: "linear-gradient(135deg, #1a5fa8 50%, #f5c518 50%)", border: "#1a5fa8" },
      "Yellow-Blue": { bg: "linear-gradient(135deg, #f5c518 50%, #1a5fa8 50%)", border: "#f5c518" },
    };

    render() {
      this.data.levels.forEach((level) => {
        const fieldset = this.querySelector(`[data-level="${level.key}"]`);
        if (!fieldset) return;

        // Update legend text
        const legend = fieldset.querySelector("legend");
        if (legend) legend.textContent = `${level.label}:`;

        // Update selected value display
        const valueEl = fieldset.querySelector("[data-selected-value]");
        if (valueEl) {
          const unit = level.unit ? ` ${level.unit}` : "";
          valueEl.textContent = this.current[level.key] + unit;
        }

        const optionsEl = fieldset.querySelector(".variant-picker__option-values");
        if (!optionsEl) return;

        // Clear existing pills safely
        while (optionsEl.firstChild) {
          optionsEl.removeChild(optionsEl.firstChild);
        }

        const values = this.getValues(level.key);

        values.forEach((value) => {
          const pill = document.createElement("button");
          pill.type = "button";
          pill.className = "block-swatch";

          const isSelected = this.current[level.key] === value;
          if (isSelected) {
            pill.classList.add("is-selected");
          }

          const available = this.isAvailable(level.key, value);
          if (!available) {
            pill.classList.add("is-disabled");
          }

          pill.setAttribute("role", "radio");
          pill.setAttribute("aria-label", `${level.label}: ${value}${level.unit ? " " + level.unit : ""}`);
          pill.setAttribute("aria-checked", isSelected ? "true" : "false");

          // Render color swatches for color level
          const swatch = WingVariantPicker.SWATCHES[value];
          if (level.key === "color" && swatch) {
            const dot = document.createElement("span");
            dot.className = "wing-swatch-dot";
            dot.style.background = swatch.bg;
            dot.style.borderColor = swatch.border;
            pill.appendChild(dot);
          }

          const displayValue = level.key === "size" ? `${value} ${level.unit || ""}`.trim() : value;
          const note = level.value_notes && level.value_notes[value];

          if (note) {
            pill.classList.add("has-note");
            const wrapper = document.createElement("span");
            wrapper.className = "wing-pill-content";
            wrapper.appendChild(document.createTextNode(displayValue));
            const noteEl = document.createElement("span");
            noteEl.className = "wing-pill-note";
            noteEl.textContent = note;
            wrapper.appendChild(noteEl);
            pill.appendChild(wrapper);
          } else {
            pill.appendChild(document.createTextNode(displayValue));
          }

          pill.addEventListener("click", () => this.handleSelect(level.key, value));
          optionsEl.appendChild(pill);
        });
      });
    }

    handleSelect(levelKey, value) {
      if (this.current[levelKey] === value) return;

      this.current[levelKey] = value;
      const key = this.getMatrixKey();
      const entry = this.data.matrix[key];

      if (entry && entry.handle) {
        window.location.href = `/products/${entry.handle}`;
      } else {
        // Re-render to show updated state even if no valid combo
        this.render();
      }
    }
  }

  customElements.define("wing-variant-picker", WingVariantPicker);
}
